import React, {useEffect, useState} from 'react';
import './styles/PowerBiApp.css';
import {default as axios} from "axios";
import {models} from "powerbi-client";
import {PowerBIEmbed} from "powerbi-client-react";
import {useMsal} from "@azure/msal-react";
import {scopeBase} from "../Config";
import {InteractionRequiredAuthError, InteractionStatus} from "@azure/msal-browser";
import {useHistory, useLocation} from 'react-router-dom';
import {golfData} from "../reportConfigs/golfReports";
import {baseballData} from "../reportConfigs/baseballReports";
import {softballData} from "../reportConfigs/softballReports";
import {footballData} from "../reportConfigs/footballReports";
import {americanFootballData} from "../reportConfigs/americanFootballReports";

// Define an object mapping variable values to imported data
const dataMap = {
    golf: golfData,
    baseball: baseballData,
    softball: softballData,
    football: footballData,
    americanFootball: americanFootballData
};



export default function PowerBiApp(props) {
    const [sideMenuItems, setSideMenuItems] = useState([]);
    const [itemToSend, setItemToSend] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [appName, setAppName] = useState(null);
    const [appType, setAppType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingToken, setLoadingToken] = useState(false);
    const [loadingTokenRetries, setLoadingTokenRetries] = useState(5);
    const [loadingTokenError, setLoadingTokenError] = useState(false);

    const {instance, inProgress, accounts} = useMsal();
    const history = useHistory();
    const params = useLocation();
    const pathParams = params?.pathname.split('/');

    // URL Path Params
    const type = pathParams[1];
    const sport = pathParams[2];
    const appId = pathParams[3] || props.location?.data?.appId;
    const w_id = pathParams[4];
    const r_id = pathParams[5];

    const acquireToken = () => {
        if (!!accessToken || inProgress !== InteractionStatus.None) return;

        setLoadingToken(true);
        const accessTokenRequest = {
            scopes: scopeBase,
            account: accounts[0],
            forceRefresh: true // Force refresh to ensure latest permissions
        };

        instance.acquireTokenSilent(accessTokenRequest)
            .then((accessTokenResponse) => {
                setAccessToken(accessTokenResponse.accessToken);
                setLoadingToken(false);
                setLoadingTokenError(false);
            })
            .catch(async (error) => {
                let retries = loadingTokenRetries - 1;
                setLoadingTokenRetries(retries);

                if (retries <= 0) {
                    try {
                        const popupToken = await instance.acquireTokenPopup(accessTokenRequest);
                        setAccessToken(popupToken.accessToken);
                        setLoadingToken(false);
                        setLoadingTokenError(false);
                    } catch (popupError) {
                        console.error("Popup login failed", popupError);
                        setLoadingTokenError(true);
                    }
                } else {
                    acquireToken();
                }
            });
    };

    useEffect(() => {
        acquireToken();
    }, [inProgress]);

    const handleClickItem = (item, token) => {
        setLoading(true);
        history.push(item.workspaceId ? `/${item.type}/${item.sport}/${item.appId}/${item.workspaceId}/${item.reportId}` : `/${item.type}/${item.sport}/${item.appId}/${item.applicationId}/${item.reportId}`);

        const url = item.workspaceId ?
            `https://api.powerbi.com/v1.0/myorg/groups/${item.workspaceId}/reports/${item.reportId}` :
            `https://api.powerbi.com/v1.0/myorg/apps/${item.applicationId}/reports/${item.reportId}`;

        axios.get(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(response => {
                item.embedURL = response.data.embedUrl;
                setItemToSend(item);
                setLoading(false);
            })
            .catch(() => {
                item.embedURL = null;
                setItemToSend(item);
                setLoading(false);
            });
    };

    useEffect(() => {
        let tempSideMenuItems = [];
        let tempAppName = null;
        let tempAppType = type;

        if (type && sport && appId && r_id && w_id) {
            const data = dataMap[sport]?.find(d => d.appId === appId && d.reports.some(r => r.reportId === r_id && (r.workspaceId === w_id || r.applicationId === w_id)));

            if (data) {
                tempSideMenuItems = data.reports.map(r => ({...r, appId, sport, type}));
                tempAppName = data.name;
                setSideMenuItems(tempSideMenuItems);
                setAppName(tempAppName);
                setAppType(tempAppType);
            } else {
                history.push(`/${type}/${sport}`);
                return;
            }
        } else {
            const storedItems = props.location?.reports || JSON.parse(localStorage.getItem("powerBI_appReports") || "[]");
            tempSideMenuItems = storedItems.map(r => ({...r, appId, sport, type}));
            setSideMenuItems(tempSideMenuItems);

            const storedName = props.location?.data?.name || JSON.parse(localStorage.getItem("powerBI_appName"));
            const storedType = props.location?.data?.type || JSON.parse(localStorage.getItem("powerBI_appType"));
            setAppName(storedName);
            setAppType(storedType);
        }
    }, [type, sport, appId]);

    useEffect(() => {
        if (accessToken && sideMenuItems.length > 0) {
            const item = sideMenuItems.find(r => r.reportId === r_id) || sideMenuItems[0];
            handleClickItem(item, accessToken);
        }
    }, [accessToken, sideMenuItems]);

    const getNewAccessToken = async () => {
        try {
            const response = await instance.acquireTokenSilent({ scopes: scopeBase, account: accounts[0], forceRefresh: true });
            setAccessToken(response.accessToken);
            return response.accessToken;
        } catch (error) {
            console.error("Token refresh failed", error);
            return null;
        }
    };

    return (
        <div className='container'>
            {loadingToken ? null : loadingTokenError ? null : (
                <>
                    <div className='split left'>
                        <h3 className='side-title centered'>{appName}</h3>
                        <ul className='side-items'>
                            {sideMenuItems.map((item, key) => (
                                <li
                                    key={key}
                                    className={`side-links ${itemToSend?.reportId === item.reportId && itemToSend?.workspaceId === item.workspaceId && 'active'}`}
                                    onClick={() => handleClickItem(item, accessToken)}
                                >
                                    <span className='report-item'>{item.name}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {!loading && (
                        <div className='right'>
                            {itemToSend ? (
                                <PowerBIEmbed
                                    key={itemToSend.reportId + accessToken}
                                    embedConfig={{
                                        type: 'report',
                                        id: itemToSend.reportId,
                                        embedUrl: itemToSend.embedURL,
                                        accessToken: accessToken,
                                        tokenType: models.TokenType.Aad,
                                        eventHooks: {
                                            accessTokenProvider: getNewAccessToken
                                        },
                                        settings: {
                                            panes: {
                                                filters: {
                                                    expanded: itemToSend.visible ?? false,
                                                    visible: itemToSend.visible ?? false
                                                }
                                            },
                                            background: models.BackgroundType.Transparent,
                                            layoutType: models.LayoutType.Custom,
                                            customLayout: {
                                                displayOption: models.DisplayOption.FitToPage
                                            }
                                        }
                                    }}
                                    eventHandlers={new Map([
                                        ['loaded', () => console.log('Report loaded')],
                                        ['rendered', () => console.log('Report rendered')],
                                        ['error', (event) => console.error(event.detail)]
                                    ])}
                                    cssClassName={(sport === 'golf' && appType === 'customer') ? 'Embed-container-split powerbi-container' : 'Embed-container-split'}
                                    getEmbeddedComponent={(embeddedReport) => {
                                        window.report = embeddedReport;
                                    }}
                                />
                            ) : (
                                <div className='report-container'>
                                    {/* Select a report */}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
